import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { CrmConfigInfo } from './crm-config-info.js';
import { CrmConnectionView } from './crm-connection-view.js';
import { HubspotLeadStatusMappingView } from './hubspot-lead-status-mapping-view.js';

// ---------------------------------------------------------------------------
// CRM baseQL operations.
//
// Replaces the previous REST endpoints
//   GET    /api/config/crm
//   GET    /api/crm/connections
//   DELETE /api/crm/connections/:id
// with first-class baseQL Query / Mutation calls so the typed callable is
// shared between client and server (the resolver runs server-side; on the
// client the same import is rewired into a network call).
//
// Lives in the shared `sdr-ui` package because both the lit pages (client)
// and the SDR entrypoint (server) need to import the same registered
// operation. The actual server implementation is injected via
// `bindCrmResolvers(...)` from `entrypoints/sdr/src/server/resolver/crm-resolvers.ts`
// so this file stays free of server-only imports (mongodb, env, etc.).
// ---------------------------------------------------------------------------

export interface CrmResolverBindings {
  getCrmConfig: () => Promise<CrmConfigInfo>;
  listCrmConnections: () => Promise<CrmConnectionView[]>;
  disconnectCrmConnection: (connectionId: string) => Promise<boolean>;
  /** Read the portal's Lead Status options + the current/deterministic-suggested mapping. */
  getHubspotLeadStatusMapping: (connectionId: string) => Promise<HubspotLeadStatusMappingView>;
  /** Same shape, but with the suggested values proposed by the LLM. */
  suggestHubspotLeadStatusMapping: (connectionId: string) => Promise<HubspotLeadStatusMappingView>;
  /** Persist the operator-confirmed mapping (JSON `{localStatus: optionValue}`). */
  saveHubspotLeadStatusMapping: (connectionId: string, mappingJson: string) => Promise<boolean>;
}

const notBound = (name: string) => () => {
  throw new Error(`CRM resolver "${name}" not bound — server forgot to call bindCrmResolvers().`);
};

let bindings: CrmResolverBindings = {
  getCrmConfig: notBound('getCrmConfig'),
  listCrmConnections: notBound('listCrmConnections'),
  disconnectCrmConnection: notBound('disconnectCrmConnection'),
  getHubspotLeadStatusMapping: notBound('getHubspotLeadStatusMapping'),
  suggestHubspotLeadStatusMapping: notBound('suggestHubspotLeadStatusMapping'),
  saveHubspotLeadStatusMapping: notBound('saveHubspotLeadStatusMapping'),
};

/**
 * Server-side wiring: called once from the SDR entrypoint to plug the real
 * resolver implementations into the operation declarations below.
 * The client never calls this; resolvers are never invoked on the client.
 */
export const bindCrmResolvers = (impls: CrmResolverBindings): void => {
  bindings = impls;
};

export const getCrmConfig = registerQuery({
  operationName: 'getCrmConfig',
  type: () => CrmConfigInfo,
  resolve: () => (_gqlFields: any) => bindings.getCrmConfig(),
});

export const listCrmConnections = registerQuery({
  operationName: 'listCrmConnections',
  type: () => [CrmConnectionView],
  resolve: () => (_gqlFields: any) => bindings.listCrmConnections(),
});

export const disconnectCrmConnection = registerMutation({
  operationName: 'disconnectCrmConnection',
  type: () => Boolean,
  params: [{ name: 'connectionId', type: () => String }],
  resolve: (connectionId: string) => (_gqlFields: any) => bindings.disconnectCrmConnection(connectionId),
});

export const getHubspotLeadStatusMapping = registerQuery({
  operationName: 'getHubspotLeadStatusMapping',
  type: () => HubspotLeadStatusMappingView,
  params: [{ name: 'connectionId', type: () => String }],
  resolve: (connectionId: string) => (_gqlFields: any) => bindings.getHubspotLeadStatusMapping(connectionId),
});

export const suggestHubspotLeadStatusMapping = registerMutation({
  operationName: 'suggestHubspotLeadStatusMapping',
  type: () => HubspotLeadStatusMappingView,
  params: [{ name: 'connectionId', type: () => String }],
  resolve: (connectionId: string) => (_gqlFields: any) => bindings.suggestHubspotLeadStatusMapping(connectionId),
});

export const saveHubspotLeadStatusMapping = registerMutation({
  operationName: 'saveHubspotLeadStatusMapping',
  type: () => Boolean,
  params: [
    { name: 'connectionId', type: () => String },
    { name: 'mappingJson', type: () => String },
  ],
  resolve: (connectionId: string, mappingJson: string) => (_gqlFields: any) =>
    bindings.saveHubspotLeadStatusMapping(connectionId, mappingJson),
});
